/**
 * Batch Signing Service for XML using SignServerTools
 *
 * Features:
 * - Sign multiple XMLs in batch with metadata (fileName, profileLinkCode)
 * - SignServerTools handles certificate selection
 * - Frontend handles backend submission
 */

const SIGN_SERVER_URL = 'http://localhost:14500';

export interface XmlDataWithMetadata {
  xmlContent: string;  // base64-encoded XML content
  fileName: string;
  profileLinkCode: string;
}

export interface SignedXmlWithMetadata {
  fileName: string;
  profileLinkCode: string;
  signedXml?: string;  // base64 signed XML
  error?: string;
}

export interface BatchSignWithMetadataResult {
  success: boolean;
  signedXmlList: SignedXmlWithMetadata[];
}

/**
 * Sign XMLs with metadata (fileName, profileLinkCode)
 * SignServerTools handles base64 decode/sign/encode - FE handles submission
 *
 * @param xmlDataList Array of {xmlContent (base64), fileName, profileLinkCode}
 * @returns Signed XMLs with metadata (base64-encoded)
 */
export async function signBatchWithMetadata(
  xmlDataList: XmlDataWithMetadata[]
): Promise<BatchSignWithMetadataResult> {
  try {
    const response = await fetch(`${SIGN_SERVER_URL}/sign/batch-metadata`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        xmlDataList,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();

      if (response.status === 503) {
        throw new Error(errorData.error || 'Không tìm thấy USB Token hoặc certificate');
      }

      throw new Error(errorData.error || 'Ký số thất bại');
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error('Phản hồi không hợp lệ từ server');
    }

    return data as BatchSignWithMetadataResult;

  } catch (error: any) {
    if (error.message?.includes('fetch') || error.message?.includes('Failed to fetch')) {
      throw new Error(
        'Không thể kết nối đến SignServerTools!\n\n' +
        'Vui lòng kiểm tra:\n' +
        '1. Server đã chạy chưa? (Xem icon ở system tray)\n' +
        '2. Port 14500 có bị chặn không?'
      );
    }

    throw error;
  }
}

/**
 * Example usage:
 *
 * // Sign XMLs with metadata (xmlContent is base64), FE handles batch submission
 * const result = await signBatchWithMetadata([
 *   { xmlContent: base64Xml1, fileName: 'HS001...', profileLinkCode: 'PT001_VS001' },
 *   { xmlContent: base64Xml2, fileName: 'HS002...', profileLinkCode: 'PT002_VS002' },
 * ]);
 *
 * // FE submits all signed XMLs in batch to backend
 * const mapSignatureContent = {};
 * result.signedXmlList.forEach(xml => {
 *   if (xml.signedXml && !xml.error) {
 *     mapSignatureContent[xml.fileName] = xml.signedXml;
 *   }
 * });
 * await sendSignedXMLBatch(mapSignatureContent);
 */
